import PropTypes from 'prop-types';
// @mui
import { getContrastRatio, useTheme } from '@mui/material/styles';
import { Box, Tooltip, Typography } from '@mui/material';
//
import Icon from 'src/components/universe/Icon';
import { UNIVERSE_FONT } from 'src/theme/universe/brand';
import {
  inkTintOf,
  onInkOf,
  toneInkOf,
  toneMarkOf,
  toneRingOf,
  toneTintOf
} from 'src/theme/universe/tones';

// ---------------------------------------------------------------------------
// A STATUS IS ITS STORED WORD (DESIGN.md §19), in one of three forms:
//
// `label` — THE STATUS LABEL (the owner, 07/10/2026 evening: "a kind of
//   special label for status which you can bring to the header and to the
//   status column too"): the stage's icon and the stored word on a tint of
//   the stage's tone, 24px, radius 6. The same object sits in every row of
//   the status column, on the phone object row, in the context facts and in
//   the pipeline strip above the list, so the strip reads as the legend of
//   the column. `filled` is the lit stage of the strip: the ink behind the
//   word. `wrap` lets the word break onto a second line where a strip cell is
//   narrow; a row never wraps it.
// `text` — the word beside an 8px mark, for menus, activity lines and prose.
// `lead` — the one pill of an identity band.
//
// `tone` is the key the status registry already assigns (the translation and
// allocation workflows' STATUS_TONE maps); the colours are the theme ramps
// those keys read today (src/theme/universe/tones.js). The mark, the icon and
// the ink are all the tone's INK, so a row and the strip above it never
// disagree about a colour (the owner, 07/10/2026).
//
// `ink` is the other way in: a vocabulary whose colour is a CATEGORY and not
// a state — a department, one of the five brands, a family of cost — hands
// its own ink from the §4 registries and gets the same object back, wash,
// line and all. A caller passes one or the other, never both.
// ---------------------------------------------------------------------------

export const toneMark = (theme, tone) => toneMarkOf(theme, tone);

export const toneInk = (theme, tone) => toneInkOf(theme, tone);

Status.propTypes = {
  value: PropTypes.string,
  tone: PropTypes.string,
  // A CATEGORICAL INK OF ITS OWN (§4) instead of a tone's: the colour of a
  // department, a brand, a cost family. The registries in
  // `src/theme/universe/tones.js` resolve it; the label's geometry, wash and
  // line are the ones every status label wears, so a vocabulary that is not a
  // state still reads as the same object in a row.
  ink: PropTypes.string,
  variant: PropTypes.oneOf(['text', 'label', 'band', 'lead']),
  // the stage's icon (a registry name, §10) — drawn by the `label` form
  icon: PropTypes.string,
  // the lit form of a label: the ink behind the word it can carry
  filled: PropTypes.bool,
  // a label that may break its word onto a second line (a narrow strip cell)
  wrap: PropTypes.bool,
  // a label that fills its column, so every row's reads the same width
  fullWidth: PropTypes.bool,
  hint: PropTypes.node,
  empty: PropTypes.string,
  size: PropTypes.number,
  sx: PropTypes.object
};

export default function Status({
  value,
  tone = 'default',
  ink: ownInk,
  variant = 'text',
  icon,
  filled = false,
  wrap = false,
  fullWidth = false,
  hint,
  empty = 'No status',
  size = 13,
  sx
}) {
  const theme = useTheme();
  const mark = ownInk || toneMark(theme, tone);
  const ink = ownInk || toneInk(theme, tone);
  // A categorical ink makes its own wash the way a tone's is made, and the
  // word on a filled one is whichever house ink the fill can carry — white on
  // a dark ink, the dark one on a light ink (a categorical ink is LIGHT on
  // the night, and white on it cannot be read).
  const tintOf = (step) =>
    ownInk ? inkTintOf(theme, ownInk, step) : toneTintOf(theme, tone, step);
  // A tone's fill keeps white, unless its ink is too light to carry it (a
  // pastel tone on the night, white at 1.7:1): then the house ink that reads.
  const white = theme.palette.common.white;
  const onInk = ownInk
    ? onInkOf(theme, ownInk)
    : getContrastRatio(ink, white) >= 3
    ? white
    : onInkOf(theme, ink);
  const word = value || empty;
  const lead = variant === 'lead';
  const label = variant === 'label';
  const band = variant === 'band';

  let body;
  if (band) {
    // THE BAND: the label at the scale of a pipeline cell (DESIGN.md §15) —
    // the same icon, word and tone as the row's label, but drawn as the
    // cell's own head, edge to edge, one fixed height across the nine cells
    // whether the word takes one line or two, so nothing inside a cell
    // floats (the owner, 07/10/2026 evening, on a pill inside the cell:
    // "there are still spacing problems, it really doesn't look good in the
    // header"). Filled is the lit stage: the ink behind the word.
    body = (
      <Box
        component="span"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          width: '100%',
          minHeight: 44,
          px: 1,
          py: 0.5,
          boxSizing: 'border-box',
          textAlign: 'center',
          color: filled ? onInk : ink,
          bgcolor: filled ? ink : tintOf('label'),
          transition: 'background-color 120ms, color 120ms',
          ...sx
        }}
      >
        {icon && (
          <Icon
            name={icon}
            size={14}
            variant="Bold"
            sx={{ color: 'inherit' }}
          />
        )}
        <Typography
          component="span"
          sx={{
            fontFamily: UNIVERSE_FONT,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.2,
            letterSpacing: 0,
            color: 'inherit',
            minWidth: 0,
            overflowWrap: 'break-word'
          }}
        >
          {word}
        </Typography>
      </Box>
    );
  } else if (label) {
    // Slim (the owner, 07/10/2026 evening: the first cut was "very bulky"):
    // 22px, a 10% wash of the tone with a thin line of the same tone to keep
    // the edge crisp, the icon at 13px and the word at 12/600 — a tag, not a
    // button. The lit form (the strip's chosen stage, a brand's own PO) is the
    // ink behind the word.
    body = (
      <Box
        component="span"
        // the native title is the fallback for a word the column may have
        // clipped; with a `hint` the Tooltip below says more, and MUI refuses
        // to wrap a child that carries a title of its own
        title={!wrap && !hint && typeof word === 'string' ? word : undefined}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: wrap || fullWidth ? 'center' : 'flex-start',
          gap: 0.625,
          minWidth: 0,
          maxWidth: '100%',
          ...(fullWidth && { width: '100%' }),
          boxSizing: 'border-box',
          px: 0.875,
          borderRadius: '6px',
          color: filled ? onInk : ink,
          bgcolor: filled ? ink : tintOf('label'),
          boxShadow: filled
            ? 'none'
            : `inset 0 0 0 1px ${tintOf('line')}`,
          ...(wrap
            ? { minHeight: 22, py: 0.375, textAlign: 'center' }
            : { height: 22 }),
          ...sx
        }}
      >
        {icon && (
          <Icon
            name={icon}
            size={13}
            variant="Bold"
            sx={{ color: 'inherit' }}
          />
        )}
        <Typography
          component="span"
          noWrap={!wrap}
          sx={{
            fontFamily: UNIVERSE_FONT,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: wrap ? 1.2 : 1,
            letterSpacing: 0,
            color: 'inherit',
            minWidth: 0,
            // a wrapped word breaks at its spaces; inside a word only when
            // the word alone is wider than the cell
            ...(wrap && { overflowWrap: 'break-word' })
          }}
        >
          {word}
        </Typography>
      </Box>
    );
  } else {
    const dot = (
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 8,
          height: 8,
          flex: 'none',
          borderRadius: '50%',
          ...(mark
            ? { bgcolor: mark }
            : {
                border: `1.5px solid ${toneRingOf(theme)}`,
                bgcolor: 'transparent'
              })
        }}
      />
    );
    body = (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          minWidth: 0,
          maxWidth: 1,
          ...(lead && {
            height: 24,
            px: 1.25,
            borderRadius: 999,
            bgcolor: tintOf('rest')
          }),
          ...sx
        }}
      >
        {dot}
        <Typography
          component="span"
          noWrap
          title={!hint && typeof word === 'string' ? word : undefined}
          sx={{
            fontFamily: UNIVERSE_FONT,
            fontSize: size,
            fontWeight: lead ? 600 : 500,
            lineHeight: 1.3,
            color: lead ? ink : value ? 'text.primary' : 'text.secondary',
            minWidth: 0
          }}
        >
          {word}
        </Typography>
      </Box>
    );
  }

  if (!hint) return body;
  return (
    <Tooltip arrow title={hint}>
      {body}
    </Tooltip>
  );
}
